/**
 * How ZOOM'S OWN CONSOLE prints one plan line on its "Current Plans" table — the Subscription tab's
 * cells (wave 4, 2026-09-19). Rules: API repo docs/ZOOM-DISPLAY-PARITY.md (T2–T7); evidence: API repo
 * docs/ZOOM-DISPLAY-CORPUS.md (transcribed screenshots of Zoom's console). Every string below is one the
 * corpus records verbatim, or is derived from a SKU by a rule the corpus shows on a named account.
 *
 * WHY A SEPARATE MODULE AND NOT A CHANGE TO planName(). T3 says Zoom's Plan Name carries neither term
 * nor capacity ("Zoom Workplace Pro" + Billing Period "Annual"; "Large Meeting" + Details "500
 * participants"). That is only safe where the term and the capacity are printed in cells of their own.
 * planName() is read by surfaces with NO Billing Period and NO Details column (worklists, the Users-tab
 * License cell, the Usage tab, report captions) — stripping the term there would LOSE it. So planName()
 * keeps its meaning for every caller and productName() is the Subscription tab's own function.
 *
 * Zoom writes "--" in an empty cell. The portal's empty cell is "—", and wave 1–3 already print Zoom's
 * "--" as "—" (T2's shell Quantity). The same convention holds here: a "--" in the corpus is a "—" below.
 */

import { billingPeriod, planName } from "./planNames";

/** The fields of an API subscription line these helpers read (a subset of api/types.ts AddonRow). */
export interface ZoomLine {
  plan_category: string | null;
  plan_type: string | null;
  is_fixed_end?: boolean;
  is_child_line?: boolean;
}

/**
 * T3 · exact product names the corpus (or ZPCD's sku_name_map) records for a SKU whose planName() is a
 * raw code, a price-list name Zoom's console does not use, or carries a capacity in the name.
 */
const PRODUCT_NAMES: Record<string, string> = {
  // Corpus "Round 4": STATE RAILWAY OF THAILAND / Suan Sunandha (bare `trial` base) -> "Free Trial".
  trial: "Free Trial",
  // sku_name_map: `monthly` = "Zoom Workplace Pro Monthly" (the term sibling of `yearly`). The account
  // type of all 4 such mirror accounts is "Pro" and their dates are month-long renewals, not a trial.
  // CONFIRMED by the corpus round 2026-09-19 (W4-Q3): JustCo "Zoom Workplace Pro | … | Monthly".
  monthly: "Zoom Workplace Pro",
  // Healthcare Accreditation Institute (pro_freetrial) -> "Zoom Workplace Pro".
  pro_freetrial: "Zoom Workplace Pro",
  // sku_name_map: zoent_freetrial = "Zoom Workplace Enterprise (free trial)".
  zoent_freetrial: "Zoom Workplace Enterprise",
  // Central Group / Skillpower / Thammasat (phone_payongo) and NTT Data (phone_free_trial): the shell
  // row is "Zoom Phone"; "Pay As You Go Plan" is its DETAILS, not its name.
  phone_payongo: "Zoom Phone",
  phone_free_trial: "Zoom Phone",
  // Land Development / BCN Ratchaburi / Healthcare Accreditation (PLANCODE_1514) -> "Large Meeting".
  PLANCODE_1514: "Large Meeting",
  // Thairung (zkw_freetrial) -> "Zoom Revenue Accelerator".
  zkw_freetrial: "Zoom Revenue Accelerator",
  // Finnomena — the corpus names the SKU itself: eventplus500_freetrial -> "Zoom Events".
  eventplus500_freetrial: "Zoom Events",
  // DOA / Yuanta (ze_500_freetrial) -> "Zoom Events Unlimited".
  ze_500_freetrial: "Zoom Events Unlimited",
  // Yuanta (zec_500_freetrial) -> "Zoom Webinars Plus Unlimited".
  zec_500_freetrial: "Zoom Webinars Plus Unlimited",
  // FlowAccount (zccnb_freetrial) -> "Zoom Contact Center Essentials".
  zccnb_freetrial: "Zoom Contact Center Essentials",
  // Walailak (cmr_free_trial) -> "Cloud Recording Storage".
  cmr_free_trial: "Cloud Recording Storage",
  // FlowAccount (zp_power_pack_yearly) -> "Zoom Phone Customer Engagement Pack".
  zp_power_pack_yearly: "Zoom Phone Customer Engagement Pack",
  // Rakluke / ITOCHU / MT FOOD / Principal Healthcare / Thailand MOOC.
  zobp_pro_global_select_freetrial: "Zoom Workplace Business Plus - Global Select Calling Plan",
  // Court of Justice (legacy Active-Host bundle) -> "Enterprise Active Host".
  "ACT-ENT-LMT_500-WBN_500-T_3-yearly": "Enterprise Active Host",
  // KMUTT / Sripatum (edu20) -> "Education".
  edu20: "Education",
};

/** T3 · SKU families whose Zoom product name the corpus shows for one or more members. */
const PRODUCT_FAMILIES: ReadonlyArray<[RegExp, string]> = [
  // KMUTT large500, Sripatum / Court of Justice large1000, BCN large500 + PLANCODE_1514.
  [/^(plan_)?large\d+_/i, "Large Meeting"],
  // Finnomena webinar500, Thailand MOOC webinar3000.
  [/^(plan_)?webinar\d+_/i, "Webinar"],
  // KMUTT / ITOCHU zroom_yearly.
  [/^(zroom_|plan_zoomroom_)/i, "Zoom Rooms"],
  // Court of Justice roomconnector_yearly -> "Conference Room Connector".
  [/^(roomconnector_|plan_rc_)/i, "Conference Room Connector"],
  // ITOCHU zwr_yearly -> "Workspace Reservation".
  [/^(zwr_|workspace_reservation_)/i, "Workspace Reservation"],
  // LDC Dental / KMUTT: the Global Select calling line; must come before the plain Pro family.
  [/^phone_calling_pro_global_/i, "Zoom Phone Pro Global Select"],
  // Skillpower / FlowAccount / LDC / NTT: every term of the Pro calling line is "Zoom Phone Pro". The
  // corpus shows the one_year / three_year / free_trial terms, and — round 2026-09-19 — the monthly one:
  // JustCo "--Zoom Phone Pro | 8 licenses | -- | Monthly" (`phone_calling_pro_monthly`, W4-Q8 confirmed;
  // planName() prints sku_name_map's "Zoom Phone Pro Monthly" on every other surface).
  [/^phone_calling_pro_/i, "Zoom Phone Pro"],
];

/**
 * A TERM at the end of a resolved planName(): the price list's "Annual", "Three Year Prepay",
 * "2 Year Prepay", "Three Years Prepay", and planName's own derived "(Free Trial)" / "(Monthly)" forms.
 * Only a TRAILING term is stripped — a name that carries its term mid-string (the paid cmr_* SKUs:
 * "Cloud Recording One Year Prepay 200 GB Monthly Usage") is left whole rather than guessed at.
 */
const TRAILING_TERM =
  /\s+(?:\((?:Free Trial|Annual|Monthly|Two Year|Three Year)\)|Annual|Monthly|(?:One|Two|Three|1|2|3)[ -]Years?(?:\s+Pre[Pp]ay)?)$/;

/**
 * T3 · the Subscription tab's Plan Name — Zoom's product name, with neither term nor capacity.
 * Exact corpus name first, then a corpus-proven SKU family, then planName() with a trailing term
 * removed. Empty -> em dash (planName's own convention).
 */
export function productName(apiType: string | null | undefined): string {
  if (!apiType) return "—";
  const exact = PRODUCT_NAMES[apiType];
  if (exact) return exact;
  for (const [re, name] of PRODUCT_FAMILIES) if (re.test(apiType)) return name;
  return planName(apiType).replace(TRAILING_TERM, "");
}

/**
 * The name cell as Zoom prints it. T2 · a calling / add-on line under the Zoom Phone shell is a CHILD
 * line, which Zoom prints with a "--" prefix and no space ("--Zoom Phone Pro", "--Zoom Phone Customer
 * Engagement Pack" — NTT Data, Central Group, Skillpower, LDC Dental, FlowAccount, KMUTT). The API marks
 * those lines `is_child_line`; no other line carries the flag.
 */
export function lineName(row: ZoomLine): string {
  const name = productName(row.plan_type);
  return row.is_child_line ? `--${name}` : name;
}

/**
 * T7 · a repeated product prints its name ONCE; the following rows of the same product leave the Plan
 * Name cell blank (BCN Ratchaburi: "Large Meeting" 500 participants, then a blank name over the
 * 1,000-participant trial). Decided against the row printed directly above, so it holds under any
 * sort and on any page. Child lines are exempt: T7 is recorded for top-level lines only, and the
 * corpus's child lines always print their own name.
 */
export function repeatsProductAbove(row: ZoomLine, above: ZoomLine | undefined): boolean {
  if (!above || row.is_child_line || above.is_child_line) return false;
  return productName(row.plan_type) === productName(above.plan_type);
}

/**
 * T5 · Billing Period as Zoom prints it.
 *  - A FIXED-END line (every trial, PLANCODE_1514) prints "--" (the portal's "—"): Wall Street English,
 *    Healthcare Accreditation, Land Development, Round 4's Free Trial lines.
 *  - The Pay-As-You-Go phone shell prints "Monthly" (Central Group, Skillpower, Thammasat, LDC Dental…),
 *    although no term can be read from its SKU.
 *  - Everything else: billingPeriod() from the SKU, unchanged.
 */
export function lineBillingPeriod(row: ZoomLine): string {
  if (row.is_fixed_end) return "—";
  if (row.plan_type === "phone_payongo") return "Monthly";
  const period = billingPeriod(row.plan_type);
  return ZOOM_TERM_WORDING[period] ?? period;
}

/**
 * Zoom's own wording of a multi-year term where it differs from billingPeriod()'s label, on the
 * Billing Period cell only — planName()'s derived "(Three Year)" / "(Two Year)" on the surfaces
 * without that column is unchanged (W4-Q1 keep).
 *  - "Three Years" — W4-Q7a (owner ruling 2026-09-19): LDC Dental's `zoe_three_year` base and its
 *    `phone_calling_pro_three_year` child both read "Three Years" in the corpus.
 *  - "Two Years" — owner ruling 2026-09-19 (wave 8), by analogy: the corpus holds no two-year line, and
 *    the owner ruled the plural (e.g. `phone_calling_pro_two_year`, `plan_*_two_years`).
 */
const ZOOM_TERM_WORDING: Readonly<Record<string, string>> = {
  "Two Year": "Two Years",
  "Three Year": "Three Years",
};

/** The Zoom Workplace base products the corpus shows with Details "Bundled features". */
const BUNDLED_FEATURES_PRODUCTS = new Set([
  "Zoom Workplace Pro", // FlowAccount, DOA, Finnomena … (yearly); Healthcare Accreditation (pro_freetrial)
  "Zoom Workplace Business", // Rakluke (business_yearly); Wall Street English, Thairung (business_freetrial)
  "Zoom Workplace Enterprise", // Skillpower, Thammasat (zoe_yearly); LDC Dental (zoe_three_year)
  "Zoom Workplace Enterprise Plus", // Thailand MOOC (zoep_yearly)
  "Zoom Workplace Enterprise Essentials", // Walailak (zm_one_ent_std_free_trial)
]);

/** Details the corpus prints for one exact SKU. */
const DETAILS_BY_SKU: Record<string, readonly string[]> = {
  // Round 4: both Free Trial lines read "100 participants". Owner ruling 2026-09-19 (W4-Q5): keep.
  trial: ["100 participants"],
  // KMUTT and Sripatum, both edu20: "Education | … | 300 participants". Owner ruling 2026-09-19
  // (W4-Q5): keep.
  edu20: ["300 participants"],
  // T6 · Court of Justice: ONE row whose Details lists the bundle's components on separate lines.
  "ACT-ENT-LMT_500-WBN_500-T_3-yearly": [
    "Active hosts",
    "Large Meeting 500 participants",
    "Webinar 500 participants",
  ],
  // Central Group / Skillpower / Thammasat / LDC: the PAYG shell.
  phone_payongo: ["Pay As You Go Plan"],
  // FlowAccount's Contact Center Essentials trial.
  zccnb_freetrial: ["Bundled features"],
  // Walailak.
  cmr_free_trial: ["Cloud Recording Free Trial"],
  // W4-Q6 (corpus round 2026-09-19) · ALRO's Named Host bundle: ONE row, Details on two lines — the
  // Active Host bundle's components without its "Active hosts" line. Only this SKU is on screen; its
  // two- / three-year siblings (`ENT-LMT_500-WBN_500-twoyears` / `-threeyears`) stay unmapped.
  "ENT-LMT_500-WBN_500-yearly": ["Large Meeting 500 participants", "Webinar 500 participants"],
  // Thairung.
  zkw_freetrial: ["Free Trial"],
  // Toyota Tsusho (round 2026-09-19): "Zoom Translated Captions | 2 license(s) | Free Trial". The paid
  // `ztransl_yearly` stays "—" (Sripatum prints "--").
  ztransl_freetrial: ["Free Trial"],
  // Siriraj (round 2026-09-19): the promotional Webinar SKU reads "1,000 attendees - Promotion", NOT the
  // "1,000 participants" PARTICIPANT_CAPACITY would derive from its name. Exact entry wins over that rule.
  webinar1000_promo_annual: ["1,000 attendees - Promotion"],
  // Finnomena + Siriraj (eventplus500), DOA / Yuanta (ze_500), Yuanta (zec_500).
  eventplus500_freetrial: ["500 attendees"],
  ze_500_freetrial: ["500 attendees"],
  zec_500_freetrial: ["500 attendees"],
  // Land Development / BCN / Healthcare Accreditation: "1,000 participants".
  PLANCODE_1514: ["1,000 participants"],
};

const PARTICIPANT_CAPACITY = /^(?:plan_)?(?:large|webinar)(\d+)_/i;

/**
 * A3/A4 · the Details column, one string per printed line; null = nothing proven (rendered "—").
 * Built ONLY for the cases the corpus shows: base Workplace products ("Bundled features"), the Large
 * Meeting / Webinar capacity their SKU encodes ("500 participants", "3,000 participants"), and the exact
 * SKUs in DETAILS_BY_SKU. Deliberately null: the Global Select Calling Plan trial (Thailand MOOC's active
 * line reads "Bundled features" but Rakluke's expired one is blank — unresolved; owner ruling W4-Q4 KEEP),
 * and every Education SKU other than edu20 (no capacity in the data or the corpus).
 */
export function lineDetails(row: ZoomLine): readonly string[] | null {
  const sku = row.plan_type;
  if (!sku) return null;
  const exact = DETAILS_BY_SKU[sku];
  if (exact) return exact;
  const cap = PARTICIPANT_CAPACITY.exec(sku);
  if (cap) return [`${Number(cap[1]).toLocaleString("en-US")} participants`];
  if (
    (row.plan_category === "base" || row.plan_category === "bundle") &&
    BUNDLED_FEATURES_PRODUCTS.has(productName(sku))
  ) {
    return ["Bundled features"];
  }
  return null;
}

/**
 * W4-Q7c (owner ruling 2026-09-19) · the top-level products whose Quantity Zoom prints in the COUNTED
 * form ("7 licenses", "1 license") instead of "license(s)": ITOCHU "Workspace Reservation | 7
 * licenses" and FlowAccount "Zoom Contact Center Essentials | 7 licenses". Every other top-level line
 * in the corpus reads "license(s)". Top-level only: a Workspace Reservation printed as a CHILD of a
 * bundle reads "0 license(s)" / "30 license(s)" (Rakluke, Thailand MOOC) — the portal renders no such
 * child, and `is_child_line` (phone lines only) never marks one. Keyed on the PRODUCT, so every SKU of
 * the family gets the counted form, including ones no screenshot shows: the free trial
 * `workspace_reservation_freetrial` (17 of the 21 Workspace Reservation accounts, local mirror
 * 2026-09-19) and `zwr_three_year`. The corpus evidence is `zwr_yearly` only: ITOCHU "7 licenses", and
 * (round 2026-09-19) Chula OIT "26 licenses" and Toyota Tsusho "60 licenses".
 */
const COUNTED_LICENSE_PRODUCTS = new Set([
  "Workspace Reservation",
  "Zoom Contact Center Essentials",
]);

/**
 * T4 · the Quantity unit: Zoom Rooms print room(s), a Conference Room Connector prints ports. Keyed on
 * the PRODUCT the Plan Name cell prints as well as on the category, so the unit always agrees with the
 * name beside it — a Zoom Rooms trial that the API files as the account's `base` line (zroom_free_trial,
 * 1 local account) is named "Zoom Rooms" and must count rooms, not "1 license(s)".
 *
 * W4-Q7c · licences come in two forms. "license" = Zoom's counted form ("1 license" / "124 licenses"):
 * every phone `--` child line (NTT Data "1 license", Skillpower "124 licenses", LDC Dental "32
 * licenses", KMUTT "2 licenses") and the two top-level products in COUNTED_LICENSE_PRODUCTS.
 * "license(s)" = the form every other line prints ("10 license(s)", "18,000 license(s)").
 */
export type QuantityUnit = "license(s)" | "license" | "room" | "port";

export function quantityUnit(row: ZoomLine): QuantityUnit {
  const product = productName(row.plan_type);
  if (row.plan_category === "zoom_rooms" || product === "Zoom Rooms") return "room";
  if (row.plan_category === "room_connector" || product === "Conference Room Connector")
    return "port";
  if (row.is_child_line || COUNTED_LICENSE_PRODUCTS.has(product)) return "license";
  return "license(s)";
}
